import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './Login.css';

import config from '../config';
const API_URL = config.API_URL;

interface FormData {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    studentId: string;
    major: string;
    year: string;
    role: 'student' | 'club_leader' | 'admin';
}

const Login: React.FC = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState<FormData>({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        studentId: '',
        major: 'Computer Science',
        year: 'Graduate',
        role: 'student'
    });
    
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            navigate('/chat');
        }
    }, [navigate]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            const endpoint = isLogin ? '/auth/login' : '/auth/register';
            const payload = isLogin 
                ? { email: formData.email, password: formData.password }
                : formData;
                
            const response = await axios.post(`${API_URL}${endpoint}`, payload);
            
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            
            toast.success(isLogin ? 'Welcome back!' : 'Registration successful!');
            
            // Redirect based on role
            setTimeout(() => {
                if (response.data.user.role === 'admin') {
                    navigate('/admin');
                } else {
                    navigate('/chat');
                }
            }, 100);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="logo">
                    <img src="/pngegg.png" alt="NU Connect Logo" className="logo-img" />
                </div>
                
                <h1>NU Connect</h1>
                <p className="tagline">Connect with fellow Northeastern students via video chat</p>
                
                <div className="tabs">
                    <button 
                        className={`tab ${isLogin ? 'active' : ''}`}
                        onClick={() => setIsLogin(true)}
                    >
                        Sign In
                    </button>
                    <button 
                        className={`tab ${!isLogin ? 'active' : ''}`}
                        onClick={() => setIsLogin(false)}
                    >
                        Sign Up
                    </button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            placeholder="you@northeastern.edu"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            required
                        />
                    </div>
                    
                    {!isLogin && (
                        <>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name</label>
                                    <input
                                        type="text"
                                        placeholder="John"
                                        value={formData.firstName}
                                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                        required
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Last Name</label>
                                    <input
                                        type="text"
                                        placeholder="Doe"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="form-group">
                                <label>Student ID</label>
                                <input
                                    type="text"
                                    placeholder="9 digit ID"
                                    pattern="\d{9}"
                                    value={formData.studentId}
                                    onChange={(e) => setFormData({...formData, studentId: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Major</label>
                                    <select 
                                        value={formData.major}
                                        onChange={(e) => setFormData({...formData, major: e.target.value})}
                                        required
                                    >
                                        <option value="Computer Science">Computer Science</option>
                                        <option value="Data Science">Data Science</option>
                                        <option value="Information Systems">Information Systems</option>
                                        <option value="Engineering">Engineering</option>
                                        <option value="Business">Business</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label>Year</label>
                                    <select 
                                        value={formData.year}
                                        onChange={(e) => setFormData({...formData, year: e.target.value})}
                                        required
                                    >
                                        <option value="Freshman">Freshman</option>
                                        <option value="Sophomore">Sophomore</option>
                                        <option value="Junior">Junior</option>
                                        <option value="Senior">Senior</option>
                                        <option value="Graduate">Graduate</option>
                                        <option value="PhD">PhD</option>
                                    </select>
                                </div>
                            </div>

                            {/* Role Selection */}
                            <div className="form-group">
                                <label>Account Type</label>
                                <div className="role-selector">
                                    <label className={`role-option ${formData.role === 'student' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="role"
                                            value="student"
                                            checked={formData.role === 'student'}
                                            onChange={(e) => setFormData({...formData, role: 'student'})}
                                        />
                                        <div className="role-content">
                                            <span className="role-icon">🎓</span>
                                            <span className="role-name">Student</span>
                                            <span className="role-desc">Connect with peers</span>
                                        </div>
                                    </label>
                                    <label className={`role-option ${formData.role === 'club_leader' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="role"
                                            value="club_leader"
                                            checked={formData.role === 'club_leader'}
                                            onChange={() => setFormData({...formData, role: 'club_leader'})}
                                        />
                                        <div className="role-content">
                                            <span className="role-icon">👑</span>
                                            <span className="role-name">Club Leader</span>
                                            <span className="role-desc">Manage your club</span>
                                        </div>
                                    </label>
                                    <label className={`role-option ${formData.role === 'admin' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="role"
                                            value="admin"
                                            checked={formData.role === 'admin'}
                                            onChange={(e) => setFormData({...formData, role: 'admin'})}
                                        />
                                        <div className="role-content">
                                            <span className="role-icon">🛡️</span>
                                            <span className="role-name">Admin</span>
                                            <span className="role-desc">Manage platform</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </>
                    )}
                    
                    <button type="submit" className="submit-btn">
                        {isLogin ? 'Sign In' : 'Sign Up'}
                    </button>
                </form>
                
                <div className="footer-text">
                    <p>By signing in, you agree to connect only with verified Northeastern students</p>
                </div>
            </div>
        </div>
    );
};

export default Login;